import {useSignal} from "@preact/signals-react";
import {useCallback, useEffect, useRef} from "react";
import {isEmpty} from "@ui-seeker/utils";

import {BACKEND_APIS} from "@constants";
import {rawMatcherImageComparatorModalData} from "../@signals";


// How many candidates ahead and behind get their SKU image warmed up on each move. Each image
// costs a redirect through the back plus a download from the bucket, none of it cacheable by
// headers, so paging one by one was waiting ~1 s per arrow before the panel changed.
const PREFETCH_RADIUS = 2

const skuImageURL = skuID => `${BACKEND_APIS.BACKEND}/sku/image/${skuID}`


export default function useRawMatcherImageComparisonNavigator(brandOptions=[], setSelectedSKU) {
	const numOptions = brandOptions?.length
	const isOpen = !isEmpty(rawMatcherImageComparatorModalData.value)

	// Keeps the warmed-up Image objects alive so the browser's memory cache serves them to the
	// panel's <img> without a second round trip. Bounded by the options list, cleared on close.
	const prefetched = useRef(new Map())

	const prefetchAround = idx => {
		for (let d = -PREFETCH_RADIUS; d <= PREFETCH_RADIUS; d++) {
			const option = brandOptions[idx + d]
			if (!option || d === 0 || prefetched.current.has(option.skuID))
				continue
			const img = new Image()
			img.src = skuImageURL(option.skuID)
			prefetched.current.set(option.skuID, img)
		}
	}

	const localIndex = useSignal(
		Number.isInteger(rawMatcherImageComparatorModalData.value?.targetIndex)
			? rawMatcherImageComparatorModalData.value?.targetIndex
			: 0
	)

	const handleChangeModalData = idx => {
		const option = brandOptions[idx]
		if (option) {
			setSelectedSKU(option.skuID)
			const candidateName = `${option.skuName || ''} ${option.variant || ''}`.trim()
			// raw2sku options carry no thumbnail: the SKU image is served by the back, like the
			// option card does. Written as fullUrlRight because the modal is opened with that key
			// and the container prefers it, so leaving it in the spread pinned the first image.
			rawMatcherImageComparatorModalData.value = {
				...rawMatcherImageComparatorModalData.value,
				targetIndex: idx,
				title: option.skuName,
				fullUrlRight: skuImageURL(option.skuID),
				urlRight: undefined,
				candidateName: candidateName,
			}
			prefetchAround(idx)
		}
	}

	useEffect(() => {
		if (!isOpen) {
			prefetched.current.clear()
			return
		}

		const idx = Number.isInteger(rawMatcherImageComparatorModalData.value?.targetIndex)
			? rawMatcherImageComparatorModalData.value?.targetIndex
			: 0

		localIndex.value = idx;
		handleChangeModalData(idx)
	}, [isOpen, rawMatcherImageComparatorModalData.value?.targetIndex]);

	const handleNavigate = useCallback((delta) => {
		if (!numOptions)
			return

		const max = numOptions - 1
		const next = Math.min(Math.max((localIndex.value ?? 0) + delta, 0), max)
		if (next === localIndex.value)
			return
		localIndex.value = next
		handleChangeModalData(next)
	}, [numOptions])

	return {
		isOpen,
		numOptions,
		localIndex: localIndex.value,

		handleNavigate,
		handleNavigateLeft: () => handleNavigate(-1),
		handleNavigateRight: () => handleNavigate(1),
	}
}